/**
 * Sub-spec 10 Plan 4 — settings page for pet.postcard re-engagement.
 *
 * Section 1: toggle + inactivity window (original sub-spec 10 code).
 * Section 2: postcard template authoring (new in this polish pass).
 *   - TipTap WYSIWYG editor (same primitive as RunCardEditorPage).
 *   - Closed-allowlist variable picker for {{ pet.name }}, {{ customer.firstName }}, etc.
 *   - Preview against a sample context.
 *   - Save/load via /api/notification-templates (templateKey='pet.postcard', channel='email', locale='en-GB').
 *   - Falls back to the platform default when no tenant override exists.
 *
 * TipTap is lazy-loaded so unit tests rendering the full App don't pull in
 * the @tiptap chain. The enclosing App.tsx mounts this page WITHOUT lazy()
 * because PostcardsSettings itself is not imported by the root app; the
 * LazyTemplateEditor import inside THIS file handles the split.
 */
import { getNotificationTemplate, saveNotificationTemplate } from '@/lib/api';
import type { NotificationTemplate } from '@/lib/types';
import { Loader2, Mail, Save } from 'lucide-react';
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';

// Lazy-load TipTap chain to keep vitest happy.
const TipTapEditor = lazy(() =>
  import('@/components/template-editor/TipTapEditor').then((m) => ({ default: m.TipTapEditor })),
);
const Preview = lazy(() =>
  import('@/components/template-editor/Preview').then((m) => ({ default: m.Preview })),
);

// -----------------------------------------------------------------------
// Allowed fields for the postcard template (mirrors PET_POSTCARD_ALLOWED_FIELDS).
// -----------------------------------------------------------------------

const POSTCARD_ALLOWED_FIELDS: ReadonlyArray<string> = [
  'customer.firstName',
  'customer.fullName',
  'account.name',
  'pet.name',
  'pet.species',
  'booking.monthsSinceLast',
  'booking.lastVisitLocal',
];

const POSTCARD_DEFAULT_SUBJECT = 'We miss {{ pet.name }} at {{ account.name }}';

const POSTCARD_DEFAULT_BODY =
  '<p>Hi {{ customer.firstName }},</p>' +
  "<p>It's been about <strong>{{ booking.monthsSinceLast }} months</strong> since {{ pet.name }}'s last visit to {{ account.name }} (on {{ booking.lastVisitLocal }}).</p>" +
  "<p>We'd love to see {{ pet.name }} again soon — drop us a line if you'd like to book a stay, daycare day, or grooming session.</p>" +
  '<p>Hope to see you both soon,<br/>The team at {{ account.name }}</p>';

const SAMPLE_CONTEXT: Record<string, unknown> = {
  customer: { firstName: 'Alex', fullName: 'Alex Carter' },
  account: { name: 'Levern Bank Kennels' },
  pet: { name: 'Bailey', species: 'dog' },
  booking: { monthsSinceLast: 8, lastVisitLocal: '2025-09-14' },
};

// -----------------------------------------------------------------------
// Inactivity window sub-section (original code, refactored into a component)
// -----------------------------------------------------------------------

type PresetWindow = '3' | '6' | '12' | 'custom';

interface AccountResponse {
  account: { postcardSendAfterMonths: number | null };
}

function presetForValue(v: number | null): PresetWindow {
  if (v === 3) return '3';
  if (v === 6) return '6';
  if (v === 12) return '12';
  return 'custom';
}

function InactivityWindowSection() {
  const [enabled, setEnabled] = useState(false);
  const [preset, setPreset] = useState<PresetWindow>('6');
  const [customMonths, setCustomMonths] = useState<number>(6);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/me', { credentials: 'include' });
      if (!res.ok) return;
      const body = (await res.json()) as AccountResponse;
      const v = body.account.postcardSendAfterMonths;
      setEnabled(v !== null);
      if (v !== null) {
        const p = presetForValue(v);
        setPreset(p);
        if (p === 'custom') setCustomMonths(v);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      const months = enabled ? (preset === 'custom' ? customMonths : Number(preset)) : null;
      const res = await fetch('/api/me/account', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ postcardSendAfterMonths: months }),
      });
      if (!res.ok) {
        const errBody = (await res.json().catch(() => ({}))) as { details?: string[] };
        setMsg(errBody.details?.join(', ') ?? `Save failed (${res.status})`);
        return;
      }
      setMsg('Saved.');
      setTimeout(() => setMsg(null), 2500);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4 rounded-xl border border-border bg-surface p-5">
      <div>
        <h2 className="text-sm font-semibold">Inactivity window</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Customers must have an explicit marketing × email opt-in to receive postcards.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="rounded"
        />
        <span>Send postcards to inactive customers</span>
      </label>

      {enabled && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="min-w-[80px] text-muted-foreground">Send after</span>
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value as PresetWindow)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
            aria-label="Postcard window preset"
          >
            <option value="3">3 months</option>
            <option value="6">6 months</option>
            <option value="12">12 months</option>
            <option value="custom">Custom</option>
          </select>
          {preset === 'custom' && (
            <>
              <input
                type="number"
                min={1}
                max={36}
                value={customMonths}
                onChange={(e) => setCustomMonths(Number(e.target.value))}
                className="w-20 rounded-md border border-border bg-background px-3 py-2 text-sm num outline-none focus:ring-2 focus:ring-ring/30"
                aria-label="Custom months"
              />
              <span className="text-muted-foreground">months</span>
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 press disabled:opacity-50"
        >
          {saving && <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />}
          {saving ? 'Saving…' : 'Save'}
        </button>
        {msg && (
          <span className="text-sm text-emerald-600" role="status">
            {msg}
          </span>
        )}
      </div>

      {enabled && (
        <p className="text-xs text-muted-foreground">Postcards send daily at 03:00 UTC.</p>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Template editor sub-section
// -----------------------------------------------------------------------

function TemplateEditorSection() {
  const [subject, setSubject] = useState(POSTCARD_DEFAULT_SUBJECT);
  const [body, setBody] = useState(POSTCARD_DEFAULT_BODY);
  const [isDefault, setIsDefault] = useState(true);
  const [currentTemplate, setCurrentTemplate] = useState<NotificationTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getNotificationTemplate('pet.postcard', 'email', 'en-GB');
      if (result.current) {
        setCurrentTemplate(result.current);
        setSubject(result.current.subject ?? POSTCARD_DEFAULT_SUBJECT);
        setBody(result.current.body);
        setIsDefault(false);
      } else {
        setIsDefault(true);
        setSubject(POSTCARD_DEFAULT_SUBJECT);
        setBody(POSTCARD_DEFAULT_BODY);
      }
    } catch {
      // 403 means no comm.templates.read capability — silently show default
      setIsDefault(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const result = await saveNotificationTemplate({
        templateKey: 'pet.postcard',
        channel: 'email',
        locale: 'en-GB',
        subject: subject || null,
        body,
      });
      setCurrentTemplate(result.template);
      setIsDefault(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleRestoreDefault() {
    // Re-saving the platform defaults creates a tenant override that matches
    // the defaults — effectively "restoring" them while keeping an audit trail.
    // A UI reset revert: just clear currentTemplate back to hardcoded defaults.
    setSubject(POSTCARD_DEFAULT_SUBJECT);
    setBody(POSTCARD_DEFAULT_BODY);
    setCurrentTemplate(null);
    setIsDefault(true);
  }

  return (
    <div className="rounded-xl border border-border bg-surface p-5 space-y-5">
      <div>
        <h2 className="text-sm font-semibold">Postcard copy</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Customise the email your customers receive. Use{' '}
          <code className="rounded bg-secondary px-1 py-0.5 text-[11px]">{'{{ variable }}'}</code>{' '}
          placeholders — they substitute at send time.
        </p>
      </div>

      {isDefault && !currentTemplate && (
        <p className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
          Using the platform default. Edit and save to override.
        </p>
      )}

      {error && (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" strokeWidth={1.75} /> Loading…
        </p>
      ) : (
        <div className="space-y-4">
          {/* Subject line */}
          <div>
            <label
              htmlFor="postcard-subject"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Subject line
            </label>
            <input
              id="postcard-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={POSTCARD_DEFAULT_SUBJECT}
              className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
            />
          </div>

          {/* Body + preview split */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div>
              <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Body
              </p>
              <div className="rounded-md border border-border bg-background">
                <Suspense
                  fallback={
                    <div className="px-3 py-4 text-sm text-muted-foreground">Loading editor…</div>
                  }
                >
                  <TipTapEditor
                    value={body}
                    onChange={setBody}
                    allowedFields={POSTCARD_ALLOWED_FIELDS}
                    ariaLabel="Postcard body"
                  />
                </Suspense>
              </div>
            </div>

            <div>
              <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Preview (sample data)
              </p>
              <div className="rounded-md border border-border bg-white p-4 min-h-[200px]">
                <Suspense
                  fallback={<div className="text-sm text-muted-foreground">Loading preview…</div>}
                >
                  <Preview html={body} context={SAMPLE_CONTEXT} />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving || loading}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 press disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
          ) : (
            <Save className="size-3.5" strokeWidth={1.75} />
          )}
          {saving ? 'Saving…' : 'Save template'}
        </button>
        {!isDefault && currentTemplate && (
          <button
            type="button"
            onClick={() => void handleRestoreDefault()}
            disabled={saving || loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-sm font-medium hover:bg-secondary press disabled:opacity-50"
          >
            Restore default
          </button>
        )}
        {saved && (
          <span className="text-sm text-emerald-600" role="status">
            Saved.
          </span>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------

export function PostcardsSettings() {
  return (
    <div className="space-y-6">
      <header>
        <div className="flex items-center gap-2">
          <Mail className="size-5 text-muted-foreground" strokeWidth={1.75} />
          <h1 className="font-heading text-2xl font-medium tracking-tight">Pet postcards</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Automatically email opted-in customers who haven't booked in a while. Configure when
          postcards are sent and customise the email copy below.
        </p>
      </header>

      <InactivityWindowSection />
      <TemplateEditorSection />
    </div>
  );
}
